<template>
  <nav class="navbar" :class="{ scrolled: isScrolled }">
    <div class="nav-container">
      <router-link to="/" class="nav-logo">
        <img src="/xoul.png" alt="Xoul" class="logo-icon" />
        <span class="logo-text">Xoul</span>
        <a href="https://github.com/xoul-project/xoul" target="_blank" rel="noopener" class="logo-badge" @click.stop>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style="vertical-align: -2px; margin-right: 4px;">
            <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
          </svg>GitHub
        </a>
      </router-link>

      <!-- Hamburger button (mobile only) -->
      <button class="hamburger" @click="toggleMobile" :class="{ open: mobileOpen }">
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div class="nav-links" :class="{ 'mobile-open': mobileOpen }">
        <router-link to="/" class="nav-link" exact-active-class="active" @click="closeMobile">
          <span class="nav-icon">🏠</span>
          {{ $t('nav.home') }}
        </router-link>

        <span class="nav-divider"></span>

        <router-link to="/workflows" class="nav-link" active-class="active" @click="closeMobile">
          <span class="nav-icon">⚡</span>
          {{ $t('nav.workflows') }}
        </router-link>
        <router-link to="/personas" class="nav-link" active-class="active" @click="closeMobile">
          <span class="nav-icon">🎭</span>
          Personas
        </router-link>
        <router-link to="/codes" class="nav-link" active-class="active" @click="closeMobile">
          <span class="nav-icon">🐍</span>
          Codes
        </router-link>

        <span class="nav-divider"></span>

        <router-link to="/arena" class="nav-link" active-class="active" @click="closeMobile">
          <span class="nav-icon">🎮</span>
          {{ $t('nav.arena') }}
        </router-link>
        <router-link to="/download" class="nav-link" active-class="active" @click="closeMobile">
          <span class="nav-icon">📥</span>
          {{ $t('nav.download') }}
        </router-link>
        <!-- Auth: Login or Profile -->
        <router-link v-if="!user" to="/login" class="nav-link auth-link" @click="closeMobile">
          <span class="nav-icon">👤</span>
          {{ $t('nav.login') }}
        </router-link>
        <router-link v-else to="/profile" class="nav-link auth-link" active-class="active" @click="closeMobile">
          <img v-if="user.avatar_url" :src="user.avatar_url" class="nav-avatar" />
          <span v-else class="nav-icon">👤</span>
          {{ user.name || 'Profile' }}
        </router-link>

        <button class="lang-switch" @click="toggleLocale" :title="$t('lang.switch')">
          {{ currentFlag }} {{ locale === 'ko' ? 'KO' : 'EN' }}
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { auth } from '../auth'

export default {
  name: 'NavBar',
  setup() {
    const { locale, t } = useI18n()
    return { locale, t }
  },
  data() {
    return {
      isScrolled: false,
      user: null,
      mobileOpen: false,
    }
  },
  computed: {
    currentFlag() {
      return this.locale === 'ko' ? '🇰🇷' : '🇺🇸'
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.checkAuth()
    // Re-check auth on route changes (login/logout)
    this.$router.afterEach(() => {
      this.checkAuth()
      this.closeMobile()
    })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20
    },
    toggleLocale() {
      const next = this.locale === 'ko' ? 'en' : 'ko'
      this.locale = next
      localStorage.setItem('xoul-locale', next)
    },
    checkAuth() {
      this.user = auth.getUser()
    },
    toggleMobile() {
      this.mobileOpen = !this.mobileOpen
    },
    closeMobile() {
      this.mobileOpen = false
    },
  },
}
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 0 24px;
  height: 56px;
  display: flex;
  align-items: center;
  background: rgba(10, 10, 10, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid #1a1a1a;
  transition: all var(--transition);
}

.navbar.scrolled {
  background: rgba(10, 10, 10, 0.95);
  border-bottom: 1px solid #222;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #50c878;
}

.logo-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}

.logo-text {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #50c878;
}

.logo-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  background: rgba(80, 200, 120, 0.08);
  border: 1px solid #222;
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  text-decoration: none;
  transition: all var(--transition);
}

.logo-badge:hover {
  color: #50c878;
  border-color: rgba(80, 200, 120, 0.4);
  background: rgba(80, 200, 120, 0.15);
}

/* Hamburger button */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  background: none;
  border: 1px solid #333;
  border-radius: 6px;
  cursor: pointer;
  padding: 6px;
  transition: all 0.2s ease;
}

.hamburger span {
  display: block;
  width: 18px;
  height: 2px;
  background: #888;
  border-radius: 1px;
  transition: all 0.3s ease;
}

.hamburger:hover {
  border-color: #50c878;
}

.hamburger:hover span {
  background: #50c878;
}

.hamburger.open span:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
  background: #50c878;
}

.hamburger.open span:nth-child(2) {
  opacity: 0;
}

.hamburger.open span:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -5px);
  background: #50c878;
}

.nav-links {
  display: flex;
  gap: 2px;
  align-items: center;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: var(--radius-sm);
  color: #999;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: all var(--transition);
}

.nav-link:hover {
  color: #d0d0d0;
  background: rgba(255, 255, 255, 0.03);
}

.nav-link.active {
  color: #50c878;
  background: rgba(80, 200, 120, 0.08);
}

.nav-icon {
  font-size: 14px;
}

.nav-divider {
  width: 1px;
  height: 16px;
  background: #1a1a1a;
  margin: 0 4px;
}

.nav-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #50c878;
}

.lang-switch {
  background: rgba(80, 200, 120, 0.08);
  border: 1px solid rgba(80, 200, 120, 0.25);
  border-radius: var(--radius-sm);
  padding: 5px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all var(--transition);
  margin-left: 8px;
  line-height: 1;
  color: #999;
  letter-spacing: 0.5px;
}

.lang-switch:hover {
  background: rgba(80, 200, 120, 0.15);
  border-color: rgba(80, 200, 120, 0.4);
}

/* ── Mobile responsive (≤768px) ── */
@media (max-width: 768px) {
  .navbar {
    padding: 0 16px;
  }

  .logo-badge {
    display: none;
  }

  .hamburger {
    display: flex;
  }

  .nav-links {
    display: none;
    position: absolute;
    top: 56px;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    background: rgba(10, 10, 10, 0.97);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid #222;
    padding: 8px 16px 16px;
    animation: slideDown 0.25s ease;
  }

  .nav-links.mobile-open {
    display: flex;
  }

  .nav-link {
    padding: 12px 16px;
    font-size: 15px;
    border-radius: 8px;
    color: #888;
  }

  .nav-link:hover,
  .nav-link.active {
    background: rgba(80, 200, 120, 0.1);
  }

  .nav-divider {
    width: 100%;
    height: 1px;
    margin: 4px 0;
    background: #1a1a1a;
  }

  .lang-switch {
    margin: 8px 0 0;
    align-self: flex-start;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
